import { useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

/**
 * How many entries the Catalog entry leads to (DX-302). The Docs entry's count
 * is the docs hub's, from its own snapshot (useDocsSiteCount, DX-306).
 *
 * The target picture prints a count beside Catalog and Docs, which turns two
 * navigation entries into two facts: a reader sees what the portal holds
 * before deciding whether to open it. Its third count, beside Decisions, has
 * no source until the ADR index arrives (DX-310) — so that entry ships without
 * one rather than with an invented number.
 *
 * `undefined` means "not determined yet", and the caller renders nothing at
 * all. That is the distinction the home page's workspace figures draw for the
 * same reason (specs/home-page.md AC-5): a count
 * that has not arrived is not a count of zero, and printing 0 next to Catalog
 * says the portal is empty when the truth is that nobody has answered yet.
 *
 * Each count asks exactly what the page behind it asks, because a number that
 * disagrees with its own destination is worse than no number: a reader who
 * follows `Catalog 270` to a list of 117 learns that the sidebar guesses.
 *
 * So Catalog counts components and APIs — the catalog list's All tab, which is
 * what the entry leads to (DX-305).
 */

export type NavCounts = {
  catalog?: number;
};

/**
 * The last answer, kept for the lifetime of the page and tied to who asked.
 *
 * The sidebar unmounts and remounts on every navigation, so without this the
 * counts blank out and re-arrive on each page a reader opens — a number that
 * flickers on every click reads as instability, and the catalog has not
 * changed in the time it took to follow a link. The first render after a
 * navigation shows what was true a moment ago; the request still runs and
 * corrects it.
 *
 * `owner` is the identity the numbers were counted for. The catalog answers
 * per reader, so a count carried across a sign-out would show the previous
 * person's portal to the next one until the fresh request landed — briefly,
 * and briefly is enough to be wrong. A cache from another identity is dropped
 * rather than shown.
 *
 * Module scope rather than a ref, because the remount is what has to survive.
 * It is per page load, so a reload still asks.
 */
let lastKnown: { owner?: string; counts: NavCounts } = { counts: {} };

export const useNavCounts = (): NavCounts => {
  const catalogApi = useApi(catalogApiRef);
  const identityApi = useApi(identityApiRef);
  const [counts, setCounts] = useState<NavCounts>({});

  useEffect(() => {
    let active = true;

    const load = async () => {
      const { userEntityRef } = await identityApi.getBackstageIdentity();

      // Show the previous answer straight away, but only if this is the same
      // reader it was counted for.
      if (active && lastKnown.owner === userEntityRef) {
        setCounts(lastKnown.counts);
      }

      // Components and APIs, because that is the list the entry opens — the
      // catalog list's All tab, asked the same two questions (DX-305). Counting
      // every kind read 270 against a page showing 117; dropping only the
      // discovery's Location entities got it to 141, which was closer and still
      // wrong. The groups and systems in that difference are real, and they are
      // not what this entry leads to.
      const [components, apis] = await Promise.all([
        catalogApi.getEntities({
          filter: { kind: 'Component' },
          fields: ['metadata.name'],
        }),
        catalogApi.getEntities({
          filter: { kind: 'API' },
          fields: ['metadata.name'],
        }),
      ]);

      lastKnown = {
        owner: userEntityRef,
        counts: {
          catalog: components.items.length + apis.items.length,
        },
      };

      if (active) {
        setCounts(lastKnown.counts);
      }
    };

    // A failed count leaves the entries without a number rather than with a
    // wrong one. The navigation still works; it is the count that is missing,
    // and that is the honest state to show.
    load().catch(() => undefined);

    return () => {
      active = false;
    };
  }, [catalogApi, identityApi]);

  return counts;
};
